import { AxiosError, AxiosResponse } from "axios";
import { DataType, FieldType } from "@websential/cosmic-v20";
import { IEmployee, IEmployeeResponse } from "../models";
import { IGetListRequest, IResponse, IServerError } from "../models/common";

import { GridSize } from "@mui/material";
import { IDropDownListResponse } from "../models/common/IDropDownListResponse";
import { IEmployeeBank } from "../models/Employee/EmployeeBank";
import { IEmployeeListPayload } from "../models/Employee/IEmployeeListPayload";
import { IEmployeeStatus } from "../models/Employee/IEmployeeStatus";
import { format422Errors } from "../common/Common";
import http from "../common/http-commons";

// import { IFixedAndMetaField } from 'common/FlexFieldsUtils';

// import { IVendorListPayload, IVendorRequest, IVendorResponse } from 'models/vendor';
// import { CLIENT_SETUP_FORM, CLIENT_SETUP_FORM_FOR_ADMIN_USER } from 'screensLayoutData/client';

// import { IAPIKeysRequest, IAPIKeysResponse } from '../models';

// import { IEmployeeListPayload, IEmployeeRequest, IEmployeeResponse } from '../models/employee';

// import { INITIAL_PAGE_SIZE } from 'common/constants';

export const EmployeeService = {
  getEmployee: async (
    clientId: string,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<
    IEmployeeListPayload | IServerError | IResponse | IEmployeeResponse[]
  > => {
    try {
      const response: AxiosResponse<IEmployeeListPayload> =
        await http.get<IEmployeeListPayload>(
          "/v1/employee-list/?client_id=" +
            clientId +
            "&order_by=" +
            data.order_by +
            "&order_direction=" +
            data.order_direction +
            "&page=" +
            data.page +
            "&size=" +
            data.size +
            "&search_query=" +
            data.search_query,
        );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getDraftEmployee: async (
    clientId: string,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<
    IEmployeeListPayload | IServerError | IResponse | IEmployeeResponse[]
  > => {
    try {
      const response: AxiosResponse<IEmployeeListPayload> =
        await http.get<IEmployeeListPayload>(
          "/v1/employee-csv/?client_id=" + clientId,
          // +
          // "&order_by=" +
          // data.order_by +
          // "&order_direction=" +
          // data.order_direction +
          // "&page=" +
          // data.page +
          // "&size=" +
          // data.size +
          // "&search_query=" +
          // data.search_query
        );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getById: async (
    id: string | undefined,
    navigateToLogout: (path: string) => void,
  ): Promise<IEmployee | IServerError | IResponse | null | any> => {
    if (!id) return null;
    try {
      const response: AxiosResponse<IEmployee> = await http.get<IEmployee>(
        `/v1/employees/${id}/`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  create: async (
    requestPayload: any,
  ): Promise<IEmployee | IServerError | IResponse | any> => {
    if (requestPayload.id) {
      try {
        const response: AxiosResponse<IEmployee> = await http.put<IEmployee>(
          `/v1/employees/${requestPayload.id}/`,
          requestPayload,
        );

        if (response.status === 200) {
          return response;
        } else if (response.status === 400) {
          return response.data;
        } else if (response.status === 500) {
          return {
            status: false,
            message: "Something went wrong.",
            error: true,
          };
        }
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("An error occurred:", axiosError);
        const errorMessage =
          axiosError.response && typeof axiosError.response.data === "string"
            ? axiosError.response.data
            : "Unknown error";
        if (axiosError && axiosError.status === 401) {
          requestPayload.navigateToLogout("/logout");
          return {
            status: false,
            message: "UnAuthrized",
          };
        } else if (axiosError && axiosError.status === 422) {
          return {
            status: false,
            message: `Errors : \n${format422Errors(
              axiosError?.response?.data,
            )}`,
          };
        } else {
          return {
            status: false,
            message: errorMessage,
          };
        }
      }
    }

    try {
      const response: AxiosResponse<IEmployee> = await http.post<IEmployee>(
        "/v1/employees/",
        requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  delEmployee: async (
    delPayload: any,
  ): Promise<string | IServerError | IResponse | any> => {
    // return new Promise(resolve => setTimeout(() => resolve("Flex Field has been deleted successfully"), 1000));
    try {
      const response: AxiosResponse<string> = await http.delete<string>(
        `/v1/employees/${delPayload.employeeId}/`,
      );

      if (response.status === 204) {
        return "Employee has been deleted successfully";
      } else if (response.status === 200) {
        return "Employee has been deleted successfully";
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        delPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  delEmployeeBank: async (
    delPayload: any,
  ): Promise<string | IServerError | IResponse | any> => {
    // return new Promise(resolve => setTimeout(() => resolve("Flex Field has been deleted successfully"), 1000));
    try {
      const response: AxiosResponse<string> = await http.delete<string>(
        `/v1/employee-bank/${delPayload.employeeBankId}/?client_id=${delPayload.clientId}`,
      );

      if (response.status === 204) {
        return "Employee Bank has been deleted successfully";
      } else if (response.status === 200) {
        return "Employee Bank has been deleted successfully";
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        delPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  delBulkEmployeees: async (
    requestPayload: any,
  ): Promise<string | IServerError | IResponse> => {
    try {
      const response = await http.delete<string>(`/v1/employees/`, {
        data: requestPayload.employeeIds,
      });
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getEmployeeDDL: async (
    clientId: string,
  ): Promise<any | IServerError | IResponse | null> => {
    if (!clientId) return null;
    if (clientId === "all") return null; // for rollback-transaction headers
    const response: AxiosResponse<any[]> = await http.get<any[]>(
      `/v1/employees/ddl?client_id=${clientId}`,
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  getEmployeeBankDDL: async (
    clientId: string,
  ): Promise<any | IServerError | IResponse | null> => {
    const response: AxiosResponse<any[]> = await http.get<any[]>(
      `/v1/employees/ddl/for-banks/data?client_id=${clientId}`,
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  getEmployeeStatusList: async (
    clientId: string,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<
    IEmployeeListPayload | IServerError | IResponse | IEmployeeResponse[]
  > => {
    try {
      const response: AxiosResponse<IEmployeeListPayload> =
        await http.get<IEmployeeListPayload>(
          "/v1/employees-status/?client_id=" +
            clientId +
            "&order_by=" +
            data.order_by +
            "&order_direction=" +
            data.order_direction +
            "&page=" +
            data.page +
            "&size=" +
            data.size +
            "&search_query=" +
            data.search_query,
        );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  createEmployeeStatus: async (
    requestPayload: any,
  ): Promise<IEmployeeStatus | IServerError | IResponse | any> => {
    if (requestPayload.id) {
      try {
        const response: AxiosResponse<IEmployee> = await http.put<IEmployee>(
          `/v1/employees-status/${requestPayload.id}/?client_id=${requestPayload.clientId}`,
          requestPayload,
        );

        if (response.status === 200) {
          return response;
        } else if (response.status === 400) {
          return response.data;
        } else if (response.status === 500) {
          return {
            status: false,
            message: "Something went wrong.",
            error: true,
          };
        }
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("An error occurred:", axiosError);
        const errorMessage =
          axiosError.response && typeof axiosError.response.data === "string"
            ? axiosError.response.data
            : "Unknown error";
        if (axiosError && axiosError.status === 401) {
          requestPayload.navigateToLogout("/logout");
          return {
            status: false,
            message: "UnAuthrized",
          };
        } else if (axiosError && axiosError.status === 422) {
          return {
            status: false,
            message: `Errors : \n${format422Errors(
              axiosError?.response?.data,
            )}`,
          };
        } else {
          return {
            status: false,
            message: errorMessage,
          };
        }
      }
    }

    try {
      const response: AxiosResponse<IEmployee> = await http.post<IEmployee>(
        `/v1/employees-status/?client_id=${requestPayload.clientId}`,
        requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getEmployeeStatusDataById: async (
    id: string | undefined,
    clientId: string,
    navigateToLogout: (path: string) => void,
  ): Promise<IEmployee | IServerError | IResponse | null | any> => {
    if (!id) return null;
    try {
      const response: AxiosResponse<IEmployee> = await http.get<IEmployee>(
        `/v1/employees-status/${id}/?client_id=${clientId}`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getEmployeeStatusDataByEmployeeId: async (
    employeeId: string | undefined,
    clientId: string,
    navigateToLogout: (path: string) => void,
  ): Promise<IEmployee | IServerError | IResponse | null | any> => {
    if (!employeeId) return null;
    try {
      const response: AxiosResponse<IEmployee> = await http.get<IEmployee>(
        `/v1/employees-status/${employeeId}/?client_id=${clientId}`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 404) {
        return response;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 404) {
        return {
          status: false,
          message: "Not Found",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getGetEmployeeStatusDDL: async (
    clientId: string,
  ): Promise<any | IServerError | IResponse | null> => {
    const response: AxiosResponse<any[]> = await http.get<any[]>(
      `/v1/employees-status/employee-status-ddl?client_id=${clientId}`,
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
    //   return [
    // 	  {
    // 		  "value": "484e763d-9217-4d69-b2d3-9c92d2940ae5",
    // 		  "label": "Card One"
    // 	  },
    // 	  {
    // 		  "value": "78b92a59-267a-4512-9e85-6f7bf169b89a",
    // 		  "label": "Card Two"
    // 	  },]
  },
  deleteEmployeeStatusById: async (
    delPayload: any,
  ): Promise<string | IServerError | IResponse | any> => {
    // return new Promise(resolve => setTimeout(() => resolve("Flex Field has been deleted successfully"), 1000));
    try {
      const response: AxiosResponse<string> = await http.delete<string>(
        `/v1/employees-status/${delPayload.employeeStatusId}/?client_id=${delPayload.clientId}`,
      );

      if (response.status === 200) {
        return "Employee Status has been deleted successfully";
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        delPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  delBulkEmployeeStatuses: async (
    requestPayload: any,
  ): Promise<string | IServerError | IResponse> => {
    try {
      const response = await http.delete<string>(`/v1/employees/`, {
        data: requestPayload.employeeIds,
      });
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getEmployeeAssignemntData: async (
    employeeId: any,
    clientId: string,
    desisgnLabel: string | number,
    navigateToLogout: (path: string) => void,
  ): Promise<IEmployee | IServerError | IResponse | null | any> => {
    if (!employeeId) return null;
    try {
      const response: AxiosResponse<IEmployee> = await http.get<IEmployee>(
        `/v1/assign-employee/get-assign-employee?employee_id=${employeeId}&client_id=${clientId}&desisgn_label=${desisgnLabel}`,
      ); // change url later

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }

    // return {
    // 	"items": [
    // 	  {
    // 		"companyDesignId": "67e39a372259e7121335f1a7",
    // 		"clientId": "67e25c72334ce818118067d4",
    // 		"id": "67e4fe2bbee5cda77cf6dadc",
    // 		"employeeId": "67e25ceb334ce818118067d6",
    // 		"fromDate": "2025-03-27",
    // 		"toDate": "2025-03-31",
    // 		"notes": "string",
    // 		"designLabel": "department"
    // 	  },
    // 	  {
    // 		"companyDesignId": "67e39a372259e7121335f1a7",
    // 		"clientId": "67e25c72334ce818118067d4",
    // 		"id": "67e4ff65c3512b3f3fc72fc9",
    // 		"employeeId": "67e25ceb334ce818118067d6",
    // 		"fromDate": "2025-03-27",
    // 		"toDate": "2025-03-31",
    // 		"notes": "string",
    // 		"designLabel": "department"
    // 	  }
    // 	]
    //   }
  },
  createEmployeeAssignemnt: async (
    requestPayload: any,
  ): Promise<IEmployeeStatus | IServerError | IResponse | any> => {
    // if (requestPayload.id) {

    // 	try {
    // 		const response: AxiosResponse<IEmployee> = await http.put<IEmployee>(
    // 			`/v1/employees-assignemnt/${requestPayload.id}/`,
    // 			requestPayload
    // 		);

    // 		if (response.status === 200) {
    // 			return {
    // 				status: true,
    // 				message: "Success",
    // 				error: false,
    // 			};
    // 		} else if (response.status === 400) {
    // 			return response.data;
    // 		} else if (response.status === 500) {
    // 			return {
    // 				status: false,
    // 				message: "Something went wrong.",
    // 				error: true,
    // 			};
    // 		}
    // 	} catch (error) {
    // 		const axiosError = error as AxiosError;
    // 		console.error("An error occurred:", axiosError);
    // 		const errorMessage =
    // 			axiosError.response && typeof axiosError.response.data === "string"
    // 				? axiosError.response.data
    // 				: "Unknown error";
    // 		if (axiosError && axiosError.status === 401) {
    // 			requestPayload.navigateToLogout("/logout");
    // 			return {
    // 				status: false,
    // 				message: "UnAuthrized",
    // 			};
    // 		} else if (axiosError && axiosError.status === 422) {
    // 						return {
    // 							status: false,
    // 							message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
    // 						};
    // 		} else {

    // 			return {
    // 				status: false,
    // 				message: errorMessage,
    // 			};
    // 		}
    // 	}
    // }

    try {
      const response: AxiosResponse<IEmployee> = await http.put<IEmployee>(
        `/v1/assign-employee/assign-employee/`,
        requestPayload.items,
      );

      if (response.status === 200) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  delAssignEmployee: async (
    delPayload: any,
  ): Promise<string | IServerError | IResponse | any> => {
    // return new Promise(resolve => setTimeout(() => resolve("Flex Field has been deleted successfully"), 1000));
    try {
      const response: AxiosResponse<string> = await http.delete<string>(
        `/v1/assign-employee/delete-association/${delPayload.id}?design_label=${delPayload.designedLabel}`,
      );

      if (response.status === 204) {
        return "Assign Employee has been deleted successfully";
      } else if (response.status === 200) {
        return "Assign Employee has been deleted successfully";
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        delPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  downloadEmployeeSampleFile: async (
    clientId: string,
    taxYear: Number,
  ): Promise<IServerError | IResponse | any> => {
    const url = `/v1/employee-csv/download-csv/${clientId}?tax_year=${taxYear}`;
    const response: any = await http.post(
      url,
      null, // or {} if backend expects body
      {
        responseType: "arraybuffer", // ✅ CORRECT PLACE
      },
    );
    // const response = await http.post(url)
    if (response.status === 200) {
      return response.data;
    } else if (response.status === 400) {
      const text = new TextDecoder().decode(response.data);
      const responseObject = JSON.parse(text);
      let response400 = {
        ...response,
        data: responseObject,
      };
      return response400;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }
    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  employeeRetrieveData: async (
    requestPayload: any,
  ): Promise<string | IServerError | IResponse> => {
    const response = await http.post<string>(
      `/v1/employee-csv/import-csv/?client_id=${requestPayload.id}`,
      requestPayload.formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    if (response.status === 200) {
      return response.data;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  submitAddRemoveHeaderColumns: async (
    requestPayload: any,
  ): Promise<IEmployee | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<IEmployee> = await http.post<IEmployee>(
        `/v1/employee-list/?client_id=${requestPayload.clientId}`,
        requestPayload.columns,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 200) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  submitColumnReorder: async (
    requestPayload: any,
  ): Promise<IEmployee | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<IEmployee> = await http.post<IEmployee>(
        "/v1/employees/header-columns",
        requestPayload.headerColumns,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getDataByEmployeeId: async (
    navigateToLogout: (path: string) => void,
    employeeId: string,
    clientId: string,
    tag: number,
  ): Promise<IEmployee | IServerError | IResponse | null | any> => {
    try {
      const response: AxiosResponse<IEmployee> = await http.get<IEmployee>(
        `/v1/assign-employeeassign-employee?employee_id=${employeeId}&client_id=${clientId}&desisgn_label=${tag}`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  submitUpdatedRows: async (
    requestPayload: any,
  ): Promise<IEmployee | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<IEmployee> = await http.put<IEmployee>(
        "/v1/employee-list/",
        requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 200) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  submitUpdatedRowsFromCsv: async (
    requestPayload: any,
    temporaryStorageDataId: string,
  ): Promise<IEmployee | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<IEmployee> = await http.put<IEmployee>(
        `/v1/employee-csv/?id=${temporaryStorageDataId}&client_id=${requestPayload.clientId}`,
        requestPayload.requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 200) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  submitSaveAsDraftRowsFromCsv: async (
    requestPayload: any,
  ): Promise<IEmployee | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<IEmployee> = await http.put<IEmployee>(
        `/v1/employee-csv/?client_id=${requestPayload.clientId}`,
        requestPayload.requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 200) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  deleteDraftEmployeeById: async (
    delPayload: any,
  ): Promise<string | IServerError | IResponse | null> => {
    try {
      const response: AxiosResponse<string> = await http.delete<string>(
        // `/v1/draft-employee/${delPayload.draftEmployeeId}/`,
        `/v1/employee-csv/${delPayload.draftEmployeeId}/`,
      );

      if (response.status === 204) {
        return {
          status: true,
          message: "Draft Employee has been deleted successfully",
        };
      } else if (response.status === 200) {
        return {
          status: true,
          message: "Draft Employee has been deleted successfully",
        };
      } else if (response.status === 404) {
        return response.data;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        delPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  getPayFrequencyDDL: async (
    clientId: string,
    taxYear?: number,
  ): Promise<any | IServerError | IResponse | null> => {
    try {
      const response: AxiosResponse<IDropDownListResponse[]> = await http.get<
        IDropDownListResponse[]
      >(
        `/v1/payroll-schedule/pay-frequency-ddl/?client_id=${clientId}&tax_year=${taxYear}`,
      );
      if (response.status === 200) {
        return response.data;
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch {}
  },

  createEmployeeBank: async (
    requestPayload: any,
  ): Promise<IEmployeeBank | IServerError | IResponse | any> => {
    if (requestPayload.id) {
      try {
        const response: AxiosResponse<any> = await http.put<IEmployeeBank>(
          `/v1/employee-bank/${requestPayload.employeeId}/?client_id=${requestPayload.clientId}`,
          requestPayload,
        );

        if (response.status === 200) {
          return response;
        } else if (response.status === 400) {
          return response.data;
        } else if (response.status === 404) {
          return response.data;
        } else if (response.status === 500) {
          return {
            status: false,
            message: "Something went wrong.",
            error: true,
          };
        }
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("An error occurred:", axiosError);
        const errorMessage =
          axiosError.response && typeof axiosError.response.data === "string"
            ? axiosError.response.data
            : "Unknown error";
        if (axiosError && axiosError.status === 401) {
          requestPayload.navigateToLogout("/logout");
          return {
            status: false,
            message: "UnAuthrized",
          };
        } else {
          return {
            status: false,
            message: errorMessage,
          };
        }
      }
    }

    try {
      const response: AxiosResponse<any> = await http.post<IEmployeeBank>(
        "/v1/employee-bank/",
        requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 404) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getEmployeeBankDataById: async (
    id: string | undefined,
    clientId: string,
    navigateToLogout: (path: string) => void,
  ): Promise<IEmployeeBank | IServerError | IResponse | null | any> => {
    if (!id) return null;
    try {
      const response: AxiosResponse<IEmployee> = await http.get<IEmployee>(
        // `/v1/employee-bank/${id}/`
        `/v1/employee-bank/${id}/?client_id=${clientId}`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  getGetEmployeeBankList: async (
    clientId: string,
    data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<any | IServerError | IResponse | null> => {
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/employee-bank/?client_id=" +
          clientId +
          "&order_by=" +
          data.order_by +
          "&order_direction=" +
          data.order_direction +
          "&page=" +
          data.page +
          "&size=" +
          data.size +
          "&search_query=" +
          data.search_query,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  archiveEmployeeBank: async (
    archivePayload: any,
  ): Promise<string | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<string> = await http.post<string>(
        `/v1/employee-bank/${archivePayload.departmentsId}/`,
      );

      if (response.status === 201) {
        return "Employee Bank has been archived successfully";
      } else if (response.status === 200) {
        return "Employee Bank has been archived successfully";
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        archivePayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
};
